import Modal from "@material-ui/core/Modal/Modal";
import { Text } from "components/Typography/text.styled";
import { FontFamily, FontSize } from "config/font";
import {
  EMPLOYEE_CATEGORY_OPTIONS,
  EMPLOYMENT_OPTIONS,
  OrgWorker,
  PAYMENT_TYPE_OPTIONS,
  SALARY_OPTIONS,
  WorkerFormData,
} from "model/OrganizationalStructure";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { ButtonComponent } from "../general/button/button.component";
import { DatePickerWithLabel } from "../general/datePickerWithLabel/datePickerWithLabel.component";
import { DropdownWithLabel } from "../general/dropDownWithLabel/dropdownWithLabel.component";
import { InfoModal } from "../general/infoModal/infoModal.component";
import {
  MultiSelectOption,
  MultiSelectWithLabel,
} from "../general/multiSelectWithLabel/multiSelectWithLabel.component";
import { TextInputWithLabel } from "../general/textInput/textInput.component";
import { TopModal } from "../general/topModal/topModal.component";
import {
  clamp,
  taxTableOptions,
} from "../salary/updateWorkerSalaryModal.component";
import {
  ActionContainer,
  DetailContentContainer,
  OrgStructureWorkerModalBody,
  PhoneFieldWrapper,
} from "./organizationalStructure.styled";
import { AlertModal } from "../general/alertModal/alertModal.component";
import { CustomIcon } from "components/icon/customIcon.component";
import { Color } from "config/colors";

type Props = {
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showCreateModal: boolean;
};

export const getFirstDayOfCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}-01`;
};

export const formatDate = (date: Date | null): string => {
  if (!date) return "";
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

export const CreateWorkerModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [state, organizationalStructureActions] =
    useOrganizationalStructureStore();
  const [companyState] = useCompanyStore();
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState(false);
  const numberRegex = /^\+46[1-9]\d{8,9}$/;
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const [disableFields, setDisableFields] = useState(true);
  const [worker, setWorker] = useState<OrgWorker>();
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const companyId = companyState.company?.id;

  const employeeCategoryOptions = EMPLOYEE_CATEGORY_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const initialFormData = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    personalIdentityNumber: "",
    employmentType: undefined,
    workGroupIds: [],
    salaryType: undefined,
    salary: 0,
    startDate: getFirstDayOfCurrentMonth(),
    endDate: null,
    workPercentage: 1,
    taxTable: 36,
    paymentType: "None",
    role: "",
    employeeCategory: employeeCategoryOptions[0].value,
    employeeNumber: undefined,
  };

  const [formData, setFormData] = useState<WorkerFormData>(initialFormData);

  const deepEqual = useCallback(
    (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b),
    [],
  );

  const isFormDirty = useMemo(
    () => !deepEqual(formData, initialFormData),
    [deepEqual, formData, initialFormData],
  );

  const workgroupOptions: MultiSelectOption[] = useMemo(() => {
    return (
      state.structure?.entities?.flatMap(
        (entity) =>
          entity.units
            ?.filter((unit) => unit.name !== "Unhandled")
            ?.flatMap(
              (unit) =>
                unit.workGroups?.map((team) => ({
                  value: team.id.toString(),
                  label: unit.name.toString() + " - " + team.name,
                })) || [],
            ) || [],
      ) || []
    );
  }, [state.structure]);

  const employmentOptions = EMPLOYMENT_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const salaryOptions = SALARY_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const getEmployeeNumber = () => {
    if (!companyId) return;
    Api()
      .company.organizationalStructure.getEmployeeNumber(companyId)
      .then((res) => {
        setFormData((prev) => ({
          ...prev,
          employeeNumber: res.data,
        }));
      })
      .catch(() => {
        setFormData((prev) => ({
          ...prev,
          employeeNumber: undefined,
        }));
      });
  };

  //ABONNEMANGSREGLER

  const isConsultantEmployee =
    (formData.employmentType ?? "").toLowerCase() === "consultant";

  const isTraineeEmployee =
    (formData.employmentType ?? "").toLowerCase() === "trainee";

  const isSimflexityEmployee =
    (formData.employmentType ?? "").toLowerCase() === "simflexityemployee";

  const isProjectEmployee =
    (formData.employmentType ?? "").toLowerCase() === "projectemployee";

  const [subscription, setSubscription] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!companyId) return;
    Api()
      .company.getCompanySubscriptionInfo(companyId)
      .then((res) => {
        setSubscription(res.data.membershipDisplayName);
      })
      .catch(() => {
        setSubscription(undefined);
      });
  }, [companyState.company?.id]);

  useEffect(() => {
    if (!props.showCreateModal) return;
    getEmployeeNumber();
  }, [props.showCreateModal]);

  const isFlexCompany = useMemo(() => {
    if (subscription === undefined || subscription === null) return false;
    return subscription.includes("Flex");
  }, [subscription]);

  const hasSimflexityPay = useMemo(() => {
    const settings = companyState.company?.companySettings ?? [];
    return settings.some(
      (set) =>
        set.key === "SimflexityPay" && set.value?.toLowerCase() === "true",
    );
  }, [companyState.company?.companySettings]);

  const salaryOptionsFiltered = useMemo(() => {
    if (!isSimflexityEmployee) return salaryOptions;
    return salaryOptions.filter((opt) => opt.value !== "Monthly");
  }, [isSimflexityEmployee, salaryOptions]);

  useEffect(() => {
    if (!isSimflexityEmployee) return;

    setFormData((prev) => ({
      ...prev,
      salaryType: "Hourly",
    }));
  }, [isSimflexityEmployee, formData.salaryType]);

  const canSelectSimflexityPay = useMemo(() => {
    if (isSimflexityEmployee) return true;
    return (
      isFlexCompany && hasSimflexityPay && formData.salaryType === "Hourly"
    );
  }, [
    isSimflexityEmployee,
    isFlexCompany,
    hasSimflexityPay,
    formData.salaryType,
  ]);

  const paymentTypeOptions = useMemo(() => {
    const base = PAYMENT_TYPE_OPTIONS.map((option) => ({
      ...option,
      label: t(option.label),
    }));

    return base.filter((opt) => {
      if (opt.value === "SimflexityPay") return canSelectSimflexityPay;
      return true;
    });
  }, [t, canSelectSimflexityPay]);

  useEffect(() => {
    if (!isSimflexityEmployee) return;
    if (formData.paymentType === "SimflexityPay") return;

    setFormData((prev) => ({
      ...prev,
      paymentType: "SimflexityPay",
    }));
  }, [isSimflexityEmployee, formData.paymentType]);

  useEffect(() => {
    if (isSimflexityEmployee) return;
    if (formData.paymentType !== "SimflexityPay") return;
    if (canSelectSimflexityPay) return;

    setFormData((prev) => ({
      ...prev,
      paymentType: "None",
    }));
  }, [isSimflexityEmployee, formData.paymentType, canSelectSimflexityPay]);

  useEffect(() => {
    if (isConsultantEmployee || isTraineeEmployee || isSimflexityEmployee) {
      setFormData((prev) => ({
        ...prev,
        employeeCategory: "Undefined",
      }));
      setFormData((prev) => ({
        ...prev,
        employeeNumber: 0,
      }));
    }
  }, [formData.employmentType]);

  // --- //

  useEffect(() => {
    setDisableFields(true);
    setPhoneError("");
    setWorker(undefined);
    if (numberRegex.test(formData.phoneNumber)) {
      if (companyState.company) {
        Api()
          .company.organizationalStructure.getWorkerByNumber(
            companyState.company.id,
            formData.phoneNumber,
          )
          .then((res) => {
            setWorker(res.data);
            setLoading(false);
          })
          .catch((error) => {
            setLoading(false);
            console.log("Could not fetch worker by number", error);
          });
      }
    }
  }, [formData.phoneNumber]);

  useEffect(() => {
    if (!worker) return;

    if (worker.workerId !== 0 && worker.employmentInfo === null) {
      setFormData((prev) => ({
        ...prev,
        firstName: worker.firstName ?? "",
        lastName: worker.lastName ?? "",
        email: worker.email ?? "",
        phoneNumber: worker.phoneNumber
          ? `+${worker.phoneNumber}`
          : prev.phoneNumber,
        personalIdentityNumber: worker.personalIdentityNumber ?? "",
      }));
      setDisableFields(false);
    } else if (worker.workerId !== 0 && worker.employmentInfo !== null) {
      setPhoneError(t("OrganizationalStructure.WorkerAlreadyExists"));
    } else {
      setFormData((prev) => ({
        ...initialFormData,
        employeeNumber: prev.employeeNumber,
        phoneNumber: `+${worker.phoneNumber}`,
      }));
      setDisableFields(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker]);

  const validateFields = () => {
    const requiresEmployeeNumber = !(
      isConsultantEmployee ||
      isTraineeEmployee ||
      isSimflexityEmployee
    );

    if (isTraineeEmployee) {
      if (!formData.firstName?.trim()) return false;
      if (!formData.lastName?.trim()) return false;
      if (!formData.phoneNumber?.trim()) return false;

      if (!formData.role) return false;
      if (!formData.employmentType) return false;

      if (!formData.startDate) return false;
      if (!formData.endDate) return false;

      if (
        !Array.isArray(formData.workGroupIds) ||
        formData.workGroupIds.length === 0
      )
        return false;

      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
        return false;
      if (end <= start) return false;

      return true;
    }

    const basicFields = [
      formData.firstName?.trim(),
      formData.lastName?.trim(),
      formData.phoneNumber?.trim(),
      formData.employmentType,
      formData.salaryType,
      formData.startDate,
      formData.taxTable,
      formData.paymentType,
      formData.role,
      formData.employeeCategory,
    ];

    if (basicFields.some((field) => !field)) return false;

    if (requiresEmployeeNumber) {
      if (!formData.employeeNumber || formData.employeeNumber <= 0)
        return false;
    }

    if (
      !Array.isArray(formData.workGroupIds) ||
      formData.workGroupIds.length === 0
    )
      return false;

    if (formData.salary <= 0) return false;

    const endDateRequired = isProjectEmployee || isTraineeEmployee;
    const hasEndDate = !!formData.endDate;

    if (endDateRequired) {
      if (!formData.startDate || !hasEndDate) return false;

      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate as any);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
        return false;
      if (end <= start) return false;
    } else {
      if (hasEndDate) {
        if (!formData.startDate) return false;

        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate as any);

        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
          return false;
        if (end <= start) return false;
      }
    }

    if (formData.salaryType === "Monthly") {
      if (!formData.workPercentage || formData.workPercentage <= 0.1)
        return false;
    }

    if (isSimflexityEmployee && formData.salaryType === "Monthly") return false;
    if (isSimflexityEmployee && formData.paymentType !== "SimflexityPay")
      return false;

    if (!isSimflexityEmployee && formData.paymentType === "SimflexityPay") {
      if (!canSelectSimflexityPay) return false;
    }

    return true;
  };
  const updateFormData = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      workGroupIds: selectedTeams.map((team) => Number(team)),
    }));
  }, [selectedTeams]);

  useEffect(() => {
    updateFormData();
  }, [updateFormData]);

  const handleCreateWorker = () => {
    setLoading(true);
    const data = { ...formData };

    if (!numberRegex.test(formData.phoneNumber)) {
      setPhoneError(t("OrganizationalStructure.PhonenumberError"));
      setLoading(false);
      return;
    } else {
      setPhoneError("");
    }

    if (formData.salaryType !== "Monthly") {
      delete (data as any).workPercentage;
    }

    if (companyState.company) {
      organizationalStructureActions
        .createWorker(companyState.company.id, data)
        .then(() => {
          setShowInfo(true);
          setFormData(initialFormData);
          setLoading(false);
        })
        .catch((err: any) => {
          setError(true);
          if (err.errorMessage == "Employee number is already in use") {
            setErrorMessage(t("Alerts.EmployeeNumberIsAlreadyInUse"));
          } else {
            setErrorMessage("");
          }

          setShowInfo(true);
          setLoading(false);
          console.log("Error when creating worker:", err);
        });
    }
  };

  const closeModal = () => {
    setShowInfo(false);
    setError(false);
    setFormData(initialFormData);
    setSelectedTeams([]);
    props.setShowCreateModal(false);
    setShowAlert(false);
  };

  const handleRequestClose = useCallback(
    (_e?: unknown, reason?: "backdropClick" | "escapeKeyDown") => {
      if (reason === "backdropClick") {
        if (isFormDirty) {
          setShowAlert(true);
        } else {
          closeModal();
        }
        return;
      }

      if (reason === "escapeKeyDown") {
        if (isFormDirty) setShowAlert(true);
        else closeModal();
        return;
      }
    },
    [isFormDirty],
  );

  return (
    <>
      <Modal open={props.showCreateModal} onClose={handleRequestClose}>
        <OrgStructureWorkerModalBody>
          <TopModal
            title={t("OrganizationalStructure.CreateWorker")}
            setClose={() => (isFormDirty ? setShowAlert(true) : closeModal())}
          />
          <Text fontFamily={FontFamily.MontserratSemiBold}>
            {t("OrganizationalStructure.Details")}
          </Text>
          <DetailContentContainer>
            <DetailContentContainer>
              <PhoneFieldWrapper $highlighted={disableFields}>
                <TextInputWithLabel
                  maxLength={15}
                  label={`${t("OrganizationalStructure.Phone")} (+46XXXXXXXXX)`}
                  required={true}
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phoneNumber: e.target.value,
                    })
                  }
                  type="tel"
                  errorText={phoneError !== "" ? phoneError : ""}
                  marginBottom={10}
                  marginRight={10}
                />
              </PhoneFieldWrapper>
              <TextInputWithLabel
                maxLength={50}
                label={t("OrganizationalStructure.FirstName")}
                required={true}
                value={formData.firstName}
                fontSize={FontSize.Standard}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    firstName: e.target.value,
                  })
                }
                marginBottom={10}
                marginRight={10}
                disabled={disableFields}
              />
              <TextInputWithLabel
                maxLength={50}
                label={t("OrganizationalStructure.LastName")}
                required={true}
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    lastName: e.target.value,
                  })
                }
                marginBottom={10}
                marginRight={10}
                disabled={disableFields}
              />
              <TextInputWithLabel
                maxLength={100}
                label={t("OrganizationalStructure.Email")}
                value={formData.email}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    email: e.target.value,
                  })
                }
                type={"email"}
                marginBottom={10}
                marginRight={10}
                disabled={disableFields}
              />
              <TextInputWithLabel
                maxLength={50}
                label={t("OrganizationalStructure.PersonalIdentityNumber")}
                value={formData.personalIdentityNumber}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    personalIdentityNumber: e.target.value,
                  })
                }
                marginBottom={10}
                marginRight={10}
                disabled={disableFields}
              />
            </DetailContentContainer>
            <Text fontFamily={FontFamily.MontserratSemiBold}>
              {t("OrganizationalStructure.Employment")}
            </Text>
            <DetailContentContainer>
              <TextInputWithLabel
                maxLength={24}
                label={t("OrganizationalStructure.Role")}
                required={true}
                value={formData.role}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    role: e.target.value,
                  })
                }
                marginBottom={10}
                marginRight={10}
                disabled={disableFields}
              />
              <DatePickerWithLabel
                label={t("OrganizationalStructure.StartDate")}
                required={true}
                value={formData.startDate ? new Date(formData.startDate) : null}
                onChange={(date) =>
                  setFormData({
                    ...formData,
                    startDate: formatDate(date),
                  })
                }
                placeholder={t("OrganizationalStructure.ChooseDate")}
                dateFormat="YYYY-MM-DD"
                disabled={disableFields}
              />
              <DatePickerWithLabel
                label={t("OrganizationalStructure.EndDate")}
                value={formData.endDate ? new Date(formData.endDate) : null}
                onChange={(date) =>
                  setFormData({
                    ...formData,
                    endDate: date ? formatDate(date) : null,
                  })
                }
                placeholder={t("OrganizationalStructure.ChooseDate")}
                dateFormat="YYYY-MM-DD"
                disabled={disableFields}
                allowClear={!isProjectEmployee && !isTraineeEmployee}
                required={isProjectEmployee || isTraineeEmployee}
              />
              <DropdownWithLabel
                label={t("OrganizationalStructure.EmploymentType")}
                options={employmentOptions}
                value={formData.employmentType}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    employmentType: value,
                  })
                }
                placeholder={t("OrganizationalStructure.ChooseEmploymentType")}
                required
                disabled={disableFields}
              />
              {!isConsultantEmployee &&
                !isTraineeEmployee &&
                !isSimflexityEmployee && (
                  <DropdownWithLabel
                    label={t("OrganizationalStructure.EmployeeCategory")}
                    options={employeeCategoryOptions}
                    value={formData.employeeCategory}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        employeeCategory: value,
                      })
                    }
                    placeholder={t(
                      "OrganizationalStructure.ChooseEmployeeCategory",
                    )}
                    required
                    disabled={disableFields}
                  />
                )}
              <MultiSelectWithLabel
                label={t("OrganizationalStructure.Team/s")}
                required={true}
                options={workgroupOptions}
                values={selectedTeams}
                onChange={(values) => setSelectedTeams(values)}
                placeholder={t("OrganizationalStructure.NotAssigned")}
                maxDisplayTags={3}
                showSelectAll={true}
                disabled={disableFields}
              />
              {!isConsultantEmployee &&
                !isTraineeEmployee &&
                !isSimflexityEmployee && (
                  <>
                    <TextInputWithLabel
                      maxLength={50}
                      label={t("OrganizationalStructure.EmployeeNumber")}
                      value={formData.employeeNumber?.toString()}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          employeeNumber: Number(e.target.value),
                        })
                      }
                      marginBottom={10}
                      marginRight={10}
                      disabled={disableFields}
                      width={185}
                      required
                    />

                    <ButtonComponent
                      disabled={loading}
                      size={"small"}
                      onClick={() => getEmployeeNumber()}
                      action={true}
                      marginTop={10}
                      marginBottom={20}
                      marginRight={10}
                    >
                      <CustomIcon
                        name="reload"
                        color={Color.White}
                        padding="4px"
                        size="24px"
                      />
                    </ButtonComponent>
                  </>
                )}
              {!isTraineeEmployee && (
                <DropdownWithLabel
                  label={t("OrganizationalStructure.SalaryModel")}
                  options={salaryOptionsFiltered}
                  value={formData.salaryType}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      salaryType: value,
                    })
                  }
                  placeholder={t("OrganizationalStructure.ChooseSalaryModel")}
                  required
                  disabled={disableFields || isSimflexityEmployee}
                />
              )}
              {!isTraineeEmployee && (
                <TextInputWithLabel
                  maxLength={7}
                  label={
                    formData.salaryType === "Monthly"
                      ? t("OrganizationalStructure.Month")
                      : t("OrganizationalStructure.Hour")
                  }
                  value={formData.salary?.toString() || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      salary: parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder={t("OrganizationalStructure.AddSalary")}
                  type="number"
                  required
                  marginRight={10}
                  disabled={disableFields}
                />
              )}
              {formData.salaryType === "Monthly" && !isTraineeEmployee && (
                <TextInputWithLabel
                  maxLength={50}
                  label={`${t("OrganizationalStructure.WorkPercentage")} (%)`}
                  required={true}
                  value={
                    formData.workPercentage !== undefined &&
                    formData.workPercentage !== null
                      ? Math.round(formData.workPercentage * 100).toString()
                      : ""
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setFormData({
                        ...formData,
                        workPercentage: 0,
                      });
                      return;
                    }
                    if (!/^\d+$/.test(raw)) return;

                    const clampedPercent = clamp(parseInt(raw, 10), 0, 100);

                    setFormData({
                      ...formData,
                      workPercentage: clampedPercent / 100,
                    });
                  }}
                  type="number"
                  marginRight={10}
                  disabled={disableFields}
                />
              )}
              {!isTraineeEmployee && !isSimflexityEmployee && (
                <DropdownWithLabel
                  label={t("OrganizationalStructure.TaxTable")}
                  options={taxTableOptions}
                  value={String(formData.taxTable)}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      taxTable: Number(value),
                    })
                  }
                  placeholder={t("OrganizationalStructure.ChooseSalaryModel")}
                  required
                  disabled={disableFields}
                />
              )}

              {!isTraineeEmployee && (
                <DropdownWithLabel
                  label={t("ProfileCompany.PaymentSolution")}
                  options={paymentTypeOptions}
                  value={String(formData.paymentType)}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      paymentType: value,
                    })
                  }
                  required
                  disabled={disableFields || isSimflexityEmployee}
                />
              )}
            </DetailContentContainer>
          </DetailContentContainer>
          <ActionContainer style={{ margin: "0 auto" }}>
            <ButtonComponent
              size={"small"}
              onClick={closeModal}
              marginTop={10}
              marginBottom={20}
              marginRight={10}
            >
              {t("OrganizationalStructure.Cancel")}
            </ButtonComponent>
            <ButtonComponent
              disabled={!validateFields() || loading}
              size={"small"}
              onClick={handleCreateWorker}
              action={true}
              marginTop={10}
              marginBottom={20}
            >
              {t("OrganizationalStructure.CreateWorker")}
            </ButtonComponent>
          </ActionContainer>
        </OrgStructureWorkerModalBody>
      </Modal>
      {showInfo && (
        <InfoModal
          isOpen={showInfo}
          title={
            error
              ? t("OrganizationalStructure.SomethingWentWrong")
              : t("OrganizationalStructure.Created")
          }
          message={
            error
              ? errorMessage !== ""
                ? errorMessage
                : t("OrganizationalStructure.SomethingWentWrongInfo")
              : t("OrganizationalStructure.SuccessfullyCreatedWorker")
          }
          confirmText={t("OrganizationalStructure.Ok")}
          onConfirm={() => (error ? setShowInfo(false) : closeModal())}
          onCancel={() => (error ? setShowInfo(false) : closeModal())}
        />
      )}
      {showAlert && (
        <AlertModal
          isOpen={showAlert}
          title={t(`OrganizationalStructure.WorkerAlertTitle`)}
          message={t(`OrganizationalStructure.WorkerAlertInfo`)}
          onConfirm={() => closeModal()}
          onCancel={() => setShowAlert(false)}
          confirmText={t("OrganizationalStructure.Confirm")}
          cancelText={t("OrganizationalStructure.Cancel")}
        />
      )}
    </>
  );
};
